import React, { useEffect, useRef } from 'react';
import { Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { getTagForChangeState } from '../Common/Tags';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../helpers/utils';
import ApplicationDetail from './ApplicationDetail';
import ChangeDonut from './ChangeDonut';
import { outstandingCounts } from './constants';

/**
 * One application as a tile: what it is called, how its changes stand, how much
 * of it is actually running, and what is still outstanding.
 *
 * The count and the ring answer different questions — "how much of this is up"
 * and "is anything left to do" — so they sit side by side rather than one under
 * the other. Everything about change instances is on the ring.
 *
 * Each of the two is also the way to the list behind it. They stop the click
 * from reaching the tile, which opens the detail instead: a number that stands
 * for a list of things should go to those things.
 *
 * What is not on the tile opens under it. The detail spans the whole grid, so
 * it appears between the rows rather than stretching the tile it belongs to and
 * dragging its neighbours' heights with it.
 */
const ApplicationCard = ({
    record,
    open,
    toggle,
    scrollIntoView,
}: {
    record: any;
    open: boolean;
    toggle: () => void;
    /** the tile a deep link asked for, which brings itself into view */
    scrollIntoView?: boolean;
}) => {
    const ref = useRef<HTMLElement>(null);
    const inService = record.num_service_items_in_service ?? 0;

    useEffect(() => {
        if (scrollIntoView && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [scrollIntoView]);

    // a raw param rather than a `filter=` value: the lists prune filter values
    // their own schema does not own, and read this one in as a filter default
    const scope = `application_id=${record.id}`;

    /** a way through, so the click stops here rather than opening the tile */
    const stop = (event: React.SyntheticEvent) => event.stopPropagation();

    return (
        <>
            {/* The tile is the control: anywhere on it that is not one of the
                two links opens the detail. */}
            <article
                ref={ref}
                className={`app-card${open ? ' app-card--open' : ''}`}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-label={`${open ? 'Collapse' : 'Expand'} ${record.name}`}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <header className="app-card__head">
                    <Tooltip placement="topLeft" title={record.name}>
                        <h3 className="app-card__name">{record.name}</h3>
                    </Tooltip>
                    {record.change_state && getTagForChangeState(record.change_state)}
                </header>

                <div className="app-card__body">
                    <Tooltip title="Click to see this application's service items">
                        <Link
                            className="app-card__go app-card__count"
                            to={`/service_items?${scope}`}
                            onClick={stop}
                            onKeyDown={stop}
                        >
                            <span className="app-card__count-value">{inService}</span>
                            <span className="app-card__count-label">
                                {inService === 1 ? 'service item in service' : 'service items in service'}
                            </span>
                        </Link>
                    </Tooltip>

                    <Tooltip title="Click to see this application's change instances">
                        <Link
                            className="app-card__go app-card__chart"
                            to={`/change_instances?${scope}`}
                            onClick={stop}
                            onKeyDown={stop}
                            aria-label={`Change instances for ${record.name}`}
                        >
                            <ChangeDonut counts={outstandingCounts(record)} />
                        </Link>
                    </Tooltip>
                </div>

                {/* when it last changed, and the way in, on one line: the fact
                    leads it and the action closes it */}
                <footer className="app-card__foot">
                    <span className="app-card__when">
                        {record.modified ? (
                            <Tooltip title={formatDateAndTime(record.modified)}>
                                <span>Updated {formatDateAndTimeFromNow(record.modified)}</span>
                            </Tooltip>
                        ) : (
                            <span>Never updated</span>
                        )}
                    </span>

                    {/* what the tile does, said in words — the tile itself is
                        the button, and its label already says it to anything
                        that is not looking at the screen */}
                    <span className="app-card__more" aria-hidden="true">
                        {open ? 'Less info' : 'More info'}
                        <DownOutlined className="app-card__more-chev" />
                    </span>
                </footer>
            </article>

            {/* rendered only once open, so a page of tiles costs one detail's
                worth of rendering rather than every application's — the detail
                holds itself on screen while it animates out */}
            <ApplicationDetail record={record} open={open} />
        </>
    );
};

export default ApplicationCard;
